import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
  getLogisticsOrders,
  getSentLogisticsOrders,
  sendToLogistics,
  deleteLogisticsOrder,
} from "../../store/logistics/logistics.action";
import Pagination from "../../extra/Pagination";
import Searching from "../../extra/Searching";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import formatImageUrl from "../../extra/functions";
import defaultImage from "../../../assets/images/default.jpg";
import { getSetting } from "../../store/setting/setting.action";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import TrackingModal from "./TrackingModal";

const Logistics = () => {
  const [activeTab, setActiveTab] = useState("confirmed"); // "confirmed" or "sent"
  const [size, setSize] = useState(10);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sendingOrderId, setSendingOrderId] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedTrackingId, setSelectedTrackingId] = useState(null);

  const {
    orders,
    totalOrders,
    sentOrders,
    totalSentOrders,
    loading: reduxLoading,
  } = useSelector((state) => state.logistics);
  const { setting } = useSelector((state) => state.setting);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getSetting());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === "confirmed") {
      dispatch(getLogisticsOrders(currentPage, size, search));
    } else {
      dispatch(getSentLogisticsOrders(currentPage, size, search));
    }
  }, [dispatch, currentPage, size, activeTab]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (activeTab === "confirmed") {
      setData(orders);
    } else {
      setData(sentOrders);
    }
  }, [orders, sentOrders, activeTab]);

  // Reset pagination when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearch("");
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event), 10);
    setSize(parseInt(event), 10);
    setCurrentPage(1);
  };

  // Search handler
  const handleFilterData = (filteredData) => {
    if (typeof filteredData === "string") {
      setSearch(filteredData);
    } else {
      setData(filteredData);
    }
  };

  // Handle search submit
  const handleSearch = () => {
    setCurrentPage(1);
    if (activeTab === "confirmed") {
      dispatch(getLogisticsOrders(1, size, search));
    } else {
      dispatch(getSentLogisticsOrders(1, size, search));
    }
  };

  // Navigate to order detail
  const handleOpen = (order) => {
    navigate("/admin/order/orderDetail", {
      state: order,
    });
  };

  // Send order to logistics
  const handleSendToLogistics = (order) => {
    Swal.fire({
      title: "Send to Logistics?",
      html: `
        <p>Are you sure you want to send order <strong>${order.orderId}</strong> to logistics?</p>
        <p class="text-muted small">This will transfer the order to Howyin Logistics for delivery.</p>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Send it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setSendingOrderId(order._id);
        dispatch(sendToLogistics(order._id));
        setTimeout(() => {
          setSendingOrderId(null);
        }, 2000);
      }
    });
  };

  // Open tracking modal
  const handleViewTracking = (trackingId) => {
    setSelectedTrackingId(trackingId);
    setShowTrackingModal(true);
  };

  // Delete order
  const handleDeleteOrder = (order) => {
    Swal.fire({
      title: "Delete Order?",
      html: `
        <p>Are you sure you want to delete order <strong>${order.orderId}</strong>?</p>
        <p class="text-danger small">This will permanently delete the order from the system. This action cannot be undone!</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteLogisticsOrder(order._id, activeTab));
      }
    });
  };

  const currentTotal = activeTab === "confirmed" ? totalOrders : totalSentOrders;

  return (
    <>
      <div className="mainSellerTable">
        <div className="sellerTable">
          <div className="col-12 headname">
            <LocalShippingIcon style={{ marginRight: "8px" }} />
            Logistics Management
          </div>
          <div className="sellerMain">
            <div className="tableMain">
              {/* Tabs */}
              <div className="d-flex gap-2 mb-3 px-3 pt-3">
                <button
                  className={`btn ${
                    activeTab === "confirmed" ? "btn-warning" : "btn-outline-secondary"
                  }`}
                  onClick={() => handleTabChange("confirmed")}
                  style={{
                    padding: "10px 20px",
                    fontWeight: "500",
                    borderRadius: "8px",
                  }}
                >
                  <SendIcon style={{ fontSize: "18px", marginRight: "8px" }} />
                  Confirmed Orders ({totalOrders})
                </button>
                <button
                  className={`btn ${
                    activeTab === "sent" ? "btn-warning" : "btn-outline-secondary"
                  }`}
                  onClick={() => handleTabChange("sent")}
                  style={{
                    padding: "10px 20px",
                    fontWeight: "500",
                    borderRadius: "8px",
                  }}
                >
                  <CheckCircleIcon style={{ fontSize: "18px", marginRight: "8px" }} />
                  Sent to Logistics ({totalSentOrders})
                </button>
              </div>

              <div className="sellerHeader primeHeader">
                <div className="row">
                  <div className="col-12">
                    <Searching
                      type={`client`}
                      data={activeTab === "confirmed" ? orders : sentOrders}
                      setData={setData}
                      column={data}
                      onFilterData={handleFilterData}
                      serverSearching={handleSearch}
                      button={true}
                      setSearchValue={setSearch}
                      searchValue={search}
                      placeholder={
                        activeTab === "confirmed"
                          ? "Search by Order ID..."
                          : "Search by Order ID or Tracking ID..."
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="primeMain">
                <table
                  width="100%"
                  border
                  className="primeTable text-center"
                  style={{ maxHeight: "680px" }}
                >
                  <thead className="sticky-top" style={{ top: "-1px", zIndex: "1" }}>
                    <tr>
                      <th className="fw-bold py-3">No</th>
                      <th className="fw-bold py-3" style={{ width: "120px" }}>
                        Order Id
                      </th>
                      {activeTab === "sent" && (
                        <th className="fw-bold py-3" style={{ width: "150px" }}>
                          Tracking ID
                        </th>
                      )}
                      <th className="fw-bold py-3" style={{ width: "180px" }}>
                        Customer Info
                      </th>
                      <th className="fw-bold py-3" style={{ width: "200px" }}>
                        Shipping Address
                      </th>
                      <th className="fw-bold py-3" style={{ width: "250px" }}>
                        Items
                      </th>
                      <th className="fw-bold py-3" style={{ width: "100px" }}>
                        {`Total (${setting?.currency?.symbol || "$"})`}
                      </th>
                      <th className="fw-bold py-3" style={{ width: "80px" }}>
                        Payment
                      </th>
                      <th className="fw-bold py-3" style={{ width: "120px" }}>
                        Action
                      </th>
                      <th className="fw-bold py-3" style={{ width: "80px" }}>
                        Delete
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading || reduxLoading ? (
                      [...Array(5)].map((_, index) => (
                        <tr key={index}>
                          <td colSpan={activeTab === "sent" ? "10" : "9"}>
                            <Skeleton height={50} />
                          </td>
                        </tr>
                      ))
                    ) : data?.length > 0 ? (
                      data.map((order, index) => (
                        <tr key={order._id}>
                          {/* No */}
                          <td>
                            <span className="text-white">
                              {(currentPage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>

                          {/* Order Id */}
                          <td>
                            <span
                              className="tableBoldFont orderId"
                              onClick={() => handleOpen(order)}
                              style={{ cursor: "pointer", color: "#F0C420" }}
                            >
                              <b className="fw-normal orderIdText">{order?.orderId}</b>
                            </span>
                          </td>

                          {/* Tracking ID (only for sent tab) */}
                          {activeTab === "sent" && (
                            <td>
                              <span
                                style={{
                                  color: "#17a2b8",
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                }}
                              >
                                {order?.items?.[0]?.trackingId || "N/A"}
                              </span>
                            </td>
                          )}

                          {/* Customer Info */}
                          <td style={{ textAlign: "left" }}>
                            <div className="text-white">
                              <b style={{ fontSize: "13px" }}>
                                {order?.userId?.firstName || "Customer"}{" "}
                                {order?.userId?.lastName || ""}
                              </b>
                              <br />
                              <small className="text-muted" style={{ fontSize: "11px" }}>
                                {order?.userId?.mobileNumber
                                  ? `${order?.userId?.countryCode || ""}${order?.userId?.mobileNumber}`
                                  : order?.userId?.email || "No contact"}
                              </small>
                            </div>
                          </td>

                          {/* Shipping Address */}
                          <td style={{ textAlign: "left" }}>
                            <div className="text-white small" style={{ fontSize: "11px" }}>
                              <b>{order?.shippingAddress?.name}</b>
                              <br />
                              {order?.shippingAddress?.address?.substring(0, 30)}
                              {order?.shippingAddress?.address?.length > 30 ? "..." : ""}
                              <br />
                              <span style={{ color: "#F0C420" }}>
                                {order?.shippingAddress?.city},{" "}
                                {order?.shippingAddress?.state}
                              </span>
                            </div>
                          </td>

                          {/* Items */}
                          <td>
                            <div className="d-flex flex-column gap-1">
                              {order?.items?.slice(0, 2).map((item, itemIdx) => (
                                <div
                                  key={itemIdx}
                                  className="d-flex align-items-center gap-2"
                                >
                                  <img
                                    src={formatImageUrl(item.productId?.mainImage)}
                                    width={35}
                                    height={35}
                                    style={{
                                      borderRadius: "6px",
                                      objectFit: "cover",
                                    }}
                                    alt="Product"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = defaultImage;
                                    }}
                                  />
                                  <div className="text-start">
                                    <small
                                      className="text-white"
                                      style={{ fontSize: "11px" }}
                                    >
                                      {item.productId?.productName?.substring(0, 15)}
                                      {item.productId?.productName?.length > 15 ? "..." : ""}
                                    </small>
                                    <br />
                                    <small
                                      className="text-muted"
                                      style={{ fontSize: "10px" }}
                                    >
                                      Qty: {item.productQuantity}
                                    </small>
                                  </div>
                                </div>
                              ))}
                              {order?.items?.length > 2 && (
                                <small className="text-muted" style={{ fontSize: "10px" }}>
                                  +{order.items.length - 2} more
                                </small>
                              )}
                            </div>
                          </td>

                          {/* Total */}
                          <td>
                            <b className="text-white">{order?.finalTotal}</b>
                          </td>

                          {/* Payment Status */}
                          <td>
                            {order.paymentStatus === 1 ? (
                              <span
                                className="p-1"
                                style={{
                                  color: "#138F00",
                                  background: "#CEFAC6",
                                  borderRadius: "6px",
                                  whiteSpace: "nowrap",
                                  fontSize: "10px",
                                }}
                              >
                                COD
                              </span>
                            ) : (
                              <span
                                className="p-1"
                                style={{
                                  color: "#00A3D5",
                                  background: "#E0F5FB",
                                  borderRadius: "6px",
                                  fontSize: "10px",
                                }}
                              >
                                Paid
                              </span>
                            )}
                          </td>

                          {/* Action */}
                          <td>
                            {activeTab === "confirmed" ? (
                              <button
                                className="btn btn-success btn-sm d-flex align-items-center gap-1 mx-auto"
                                onClick={() => handleSendToLogistics(order)}
                                disabled={sendingOrderId === order._id}
                                style={{
                                  backgroundColor: "#28a745",
                                  border: "none",
                                  padding: "6px 10px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: "500",
                                }}
                              >
                                {sendingOrderId === order._id ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                    ></span>
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <LocalShippingIcon style={{ fontSize: "14px" }} />
                                    Send
                                  </>
                                )}
                              </button>
                            ) : (
                              <button
                                className="btn btn-info btn-sm d-flex align-items-center gap-1 mx-auto"
                                onClick={() =>
                                  handleViewTracking(order?.items?.[0]?.trackingId)
                                }
                                style={{
                                  backgroundColor: "#17a2b8",
                                  border: "none",
                                  padding: "6px 10px",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  color: "#fff",
                                }}
                              >
                                <VisibilityIcon style={{ fontSize: "14px" }} />
                                Track
                              </button>
                            )}
                          </td>

                          {/* Delete */}
                          <td>
                            <button
                              className="btn btn-sm d-flex align-items-center justify-content-center mx-auto"
                              onClick={() => handleDeleteOrder(order)}
                              style={{
                                backgroundColor: "transparent",
                                border: "none",
                                padding: "6px",
                                borderRadius: "50%",
                                width: "38px",
                                height: "38px",
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: "20px", color: "#dc3545" }} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={activeTab === "sent" ? "10" : "9"}
                          className="text-center text-white py-5"
                        >
                          <div>
                            <LocalShippingIcon
                              style={{
                                fontSize: "48px",
                                color: "#666",
                                marginBottom: "16px",
                              }}
                            />
                            <h5>
                              {activeTab === "confirmed"
                                ? "No Confirmed Orders"
                                : "No Orders Sent to Logistics"}
                            </h5>
                            <p className="text-muted">
                              {activeTab === "confirmed"
                                ? "Confirmed orders ready for logistics will appear here."
                                : "Orders sent to logistics will appear here."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                component="div"
                count={currentTotal}
                totalData={currentTotal}
                type={"server"}
                onPageChange={handleChangePage}
                serverPerPage={rowsPerPage}
                serverPage={currentPage}
                setCurrentPage={setCurrentPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </div>
          <div className="sellerFooter primeFooter"></div>
        </div>
      </div>

      {/* Tracking Modal */}
      {showTrackingModal && selectedTrackingId && (
        <TrackingModal
          trackingId={selectedTrackingId}
          onClose={() => {
            setShowTrackingModal(false);
            setSelectedTrackingId(null);
          }}
        />
      )}
    </>
  );
};

export default Logistics;
